import { useEffect, useState, useRef, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { apiCallErrorHandler } from '../utils/common';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/*
Props:
- vin: string (required)
- sessionId: string|number (required)
- type: 'standard' | 'full' (optional; default 'standard')
- nav: 'vin' | 'sample' (optional; default 'vin')
*/
export default function VinInspectionGrid({ vin, sessionId, type = 'standard', nav = 'vin' }) {
  const normalizedType = (type === 'full' || type === 'standard') ? type : 'standard';
  const normalizedNav = (nav === 'sample' ? 'sample' : 'vin');
  const { getAccessTokenSilently } = useAuth0();
  const toast = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [equipment, setEquipment] = useState({});
  const [sourceData, setSourceData] = useState({});
  const [showSources, setShowSources] = useState({});
  const [expandSources, setExpandSources] = useState({});
  const [navInfo, setNavInfo] = useState({
    prevVin: null,
    nextVin: null,
    prevSampleVin: null,
    nextSampleVin: null,
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!vin || !sessionId) return;
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessTokenSilently();
        const url = `${API_BASE_URL}/session/inspect/${sessionId}/${vin}/${normalizedType}`;
        const res = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) await apiCallErrorHandler(res, 'Failed to load standardized equipment');

        const payload = await res.json();
        // Expected shape from backend:
        // {
        //   Equipment: { [EnrichmentSource]: { [Key]: Value } },
        //   SourceData: { [EnrichmentSource]: string },
        //   prevVin?: string|null,
        //   nextVin?: string|null,
        //   prevSampleVin?: string|null,
        //   nextSampleVin?: string|null
        // }
        const eq = (payload?.Equipment || payload?.equipment || payload?.data?.Equipment || payload?.data?.equipment || {}) || {};
        const sd = (payload?.SourceData || payload?.sourceData || payload?.data?.SourceData || payload?.data?.sourceData || {}) || {};
        setEquipment(eq);
        setSourceData(sd);

        const prevVin = payload?.prevVin ?? payload?.data?.prevVin ?? null;
        const nextVin = payload?.nextVin ?? payload?.data?.nextVin ?? null;
        const prevSampleVin = payload?.prevSampleVin ?? payload?.data?.prevSampleVin ?? null;
        const nextSampleVin = payload?.nextSampleVin ?? payload?.data?.nextSampleVin ?? null;
        setNavInfo({ prevVin, nextVin, prevSampleVin, nextSampleVin });
      } catch (err) {
        setError(err.message || 'Failed to load');
        if (toast.current) {
          toast.current.show({ severity: 'error', summary: 'Error', detail: err.message || 'Failed to load', life: 3000 });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getAccessTokenSilently, vin, sessionId, normalizedType]);

  // Build pivot: rows keyed by attribute name, columns by enrichment source
  const { pivotRows, pivotColumns } = useMemo(() => {
    // Build list of sources as union of equipment and sourceData keys
    const eqSources = Object.keys(equipment || {});
    const sdSources = Object.keys(sourceData || {});
    const srcs = Array.from(new Set([...eqSources, ...sdSources]));
    const keySet = new Set();
    srcs.forEach((src) => {
      const entries = equipment?.[src] || {};
      Object.keys(entries).forEach((k) => keySet.add(k));
    });

    const rows = Array.from(keySet).map((k) => {
      const row = { key: k };
      srcs.forEach((src) => {
        row[src] = (equipment?.[src] || {})[k] ?? '';
      });
      return row;
    });

    const cols = [{ field: 'key', header: 'Equipment' }, ...srcs.map((s) => ({ field: s, header: s }))];

    return { pivotRows: rows, pivotColumns: cols, sources: srcs };
  }, [equipment, sourceData]);

  // Only use sourceData keys for the "Source Data" list/toggles
  const sourceList = useMemo(() => Object.keys(sourceData || {}), [sourceData]);

  // Ensure toggle state exists for current sources
  useEffect(() => {
    setShowSources((prev) => {
      const next = {};
      sourceList.forEach((s) => {
        next[s] = prev?.[s] ?? false;
      });
      return next;
    });
    setExpandSources((prev) => {
      const next = {};
      sourceList.forEach((s) => {
        next[s] = prev?.[s] ?? false;
      });
      return next;
    });
  }, [sourceList]);

  // Apply same coloring rules as MappingForm.styleForValue
  const styleForValue = (val, extra = {}) => {
    if (typeof val !== 'string') return extra;
    if (val.startsWith('✓')) return { ...extra, color: 'green', fontWeight: 600 };
    if (val.startsWith('✗')) return { ...extra, color: '#c00', fontWeight: 600 };
    if (val.startsWith('?')) return { ...extra, color: '#FF8C00' };
    return extra;
  };

  const valueBody = (val) => {
    const isColon = typeof val === 'string' && val.startsWith(':');
    const displayVal = isColon ? val.slice(1) : val;

    // If value is a file download path, render a hyperlink
    if (typeof displayVal === 'string' && displayVal.startsWith('/file/doc-download/')) {
      const href = `${(API_BASE_URL || '').replace(/\/$/, '')}${displayVal}`;
      return (
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: isColon ? '#e6ffe6' : undefined }}>
          <a href={href} target="_blank" rel="noopener noreferrer">Download</a>
        </div>
      );
    }

    const content = (displayVal === undefined || displayVal === null || displayVal === '') ? '\u00A0' : displayVal;
    const style = styleForValue(displayVal, {});
    return (
      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: isColon ? '#e6ffe6' : undefined }}>
        <span style={style}>{content}</span>
      </div>
    );
  };

  const navigateToVin = (targetVin) => {
    if (!targetVin) return;
    const { pathname, search, hash } = window.location;
    const parts = pathname.split('/').filter(Boolean);
    if (parts.length === 0) return;
    parts[parts.length - 1] = encodeURIComponent(targetVin);
    const newPath = `/${parts.join('/')}`;
    window.location.assign(`${newPath}${search}${hash}`);
  };

  const prevTarget = normalizedNav === 'sample' ? navInfo.prevSampleVin : navInfo.prevVin;
  const nextTarget = normalizedNav === 'sample' ? navInfo.nextSampleVin : navInfo.nextVin;

  const openSession = () => {
    if (!sessionId) return;
    const url = `/config/${encodeURIComponent(sessionId)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div style={{ margin: 15 }}>
      <Toast ref={toast} />
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ProgressSpinner style={{ width: '24px', height: '24px' }} strokeWidth="6" />
          <span>Loading...</span>
        </div>
      )}
      {!loading && error && (
        <div style={{ color: 'red', marginBottom: 8 }}>{error}</div>
      )}
      {!loading && !error && (
        <>
          {(!equipment || Object.keys(equipment).length === 0) ? (
            <div>No standardized equipment found for VIN {vin}.</div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontWeight: 600 }}>{normalizedType === 'full' ? 'VIN Inspection' : 'Standardized Equipment'}</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {normalizedType === 'full' && (
                      <>
                        <Button
                          type="button"
                          icon="pi pi-arrow-left"
                          label="Previous"
                          className="p-button-sm"
                          disabled={!prevTarget}
                          onClick={() => navigateToVin(prevTarget)}
                        />
                        <Button
                          type="button"
                          icon="pi pi-arrow-right"
                          label="Next"
                          className="p-button-sm"
                          disabled={!nextTarget}
                          onClick={() => navigateToVin(nextTarget)}
                        />
                        <Button
                          type="button"
                          icon="pi pi-external-link"
                          label="Open Session"
                          className="p-button-outlined p-button-sm"
                          onClick={openSession}
                        />
                      </>
                    )}
                  </div>
                </div>
                
                {sourceData && Object.keys(sourceData).length > 0 && sourceList.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ opacity: 0.8, fontSize: '0.85rem' }}>Source Data:</span>
                    {sourceList.map((s) => (
                      <Button
                        key={s}
                        type="button"
                        icon={showSources[s] ? 'pi pi-eye' : 'pi pi-eye-slash'}
                        label={s}
                        className={`p-button-sm ${showSources[s] ? '' : 'p-button-outlined'}`}
                        onClick={() => setShowSources((prev) => ({ ...prev, [s]: !prev[s] }))}
                        aria-pressed={!!showSources[s]}
                      />
                    ))}
                  </div>
                )}
              </div>
              <DataTable value={pivotRows} scrollable scrollHeight="50vh" size="small" style={{ fontSize: '0.85rem', border: '1px solid #ccc' }}>
                {pivotColumns.map((col) => (
                  col.field === 'key' ? (
                    <Column key={col.field} field={col.field} header={col.header} frozen style={{ minWidth: 160, maxWidth: 300 }} />
                  ) : (
                    <Column key={col.field} field={col.field} header={col.header} body={(row) => valueBody(row[col.field])} style={{ minWidth: 160, maxWidth: 300 }} />
                  )
                ))}
              </DataTable>

              {sourceData && Object.keys(sourceData).length > 0 && sourceList.some((s) => showSources[s]) && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>Source Data</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                    {sourceList.filter((s) => showSources[s]).map((s) => {
                      const txt = sourceData?.[s] ?? '—';
                      const expanded = !!expandSources[s];
                      const collapsedPreviewLimit = 300;
                      const isLong = typeof txt === 'string' && txt.length > collapsedPreviewLimit;
                      const preview = isLong ? `${txt.slice(0, collapsedPreviewLimit)}…` : txt;

                      const copyToClipboard = async () => {
                        try {
                          await navigator.clipboard.writeText(String(txt ?? ''));
                          toast.current?.show({ severity: 'success', summary: 'Copied', detail: `${s} source data copied`, life: 2000 });
                        } catch (e) {
                          toast.current?.show({ severity: 'warn', summary: 'Clipboard', detail: 'Copy failed', life: 2000 });
                        }
                      };

                      return (
                        <div key={s} style={{ border: '1px solid #e0e0e0', borderRadius: 6, padding: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                            <div style={{ fontWeight: 600 }}>{s}</div>
                            <div style={{ display: 'flex', gap: 6 }}>
                              {/* Removed expand/collapse button from header */}
                              <Button
                                type="button"
                                icon="pi pi-copy"
                                label="Copy"
                                className="p-button-text p-button-sm"
                                onClick={copyToClipboard}
                              />
                            </div>
                          </div>
                          <div
                            style={{
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              opacity: 0.9,
                              maxHeight: expanded ? 320 : undefined,
                              overflow: expanded ? 'auto' : 'hidden',
                              background: expanded ? '#fafafa' : undefined,
                              border: expanded ? '1px solid #eee' : undefined,
                              borderRadius: expanded ? 4 : undefined,
                              padding: expanded ? 8 : 0,
                            }}
                            title={!expanded && isLong ? 'Expand to view full content' : undefined}
                          >
                            {expanded ? (
                              <>
                                {txt}
                                {isLong && (
                                  <Button
                                    type="button"
                                    label="Show less"
                                    className="p-button-link p-button-sm"
                                    onClick={() => setExpandSources((prev) => ({ ...prev, [s]: false }))}
                                    aria-expanded={true}
                                    style={{ marginLeft: 6 }}
                                  />
                                )}
                              </>
                            ) : (
                              <>
                                {preview}<br/>
                                {isLong && (
                                  <Button
                                    type="button"
                                    label="Show more.."
                                    className="p-button-link p-button-sm"
                                    onClick={() => setExpandSources((prev) => ({ ...prev, [s]: true }))}
                                    aria-expanded={false}
                                    style={{ marginLeft: 6 }}
                                  />
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
